import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { dashboardAPI as apiConfig } from '../../../axiosConfig';
import { getEncodedURL } from '../../../utils/utils';

const URL = 'payment-options-proxy?endpoint=paymentdashboard/contextualmessage';
const STATUSES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
};

export const getContextualMessageBannerList = createAsyncThunk(
  'getContextualMessageBannerList',
  async (fetchFromDB = false, { rejectWithValue }) => {
    try {
      const url = URL;
      const { data, status } = await apiConfig.get(url);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getContextualMessagesMerchantMapping = createAsyncThunk(
  'getContextualMessagesMerchantMapping',
  async (fetchFromDB = false, { rejectWithValue }) => {
    try {
      const url = getEncodedURL(`${URL}/merchantmapping`, {
        ...(fetchFromDB && { source: 'DB' }),
        origin: 'DASHBOARD',
      });
      const { data, status } = await apiConfig.get(url);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getContextualMessagesMerchantMappingByChannelHash =
  createAsyncThunk(
    'getContextualMessagesMerchantMappingByChannelHash',
    async ({ fetchFromDB = false, channelHash }, { rejectWithValue }) => {
      try {
        const url = getEncodedURL(`${URL}/merchantmapping/${channelHash}`, {
          ...(fetchFromDB && { source: 'DB' }),
          origin: 'DASHBOARD',
        });
        const { data, status } = await apiConfig.get(url);

        if (status !== 200) {
          return rejectWithValue(data);
        }

        return data;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
  );

export const deleteContextualMessagesMerchantMapping = createAsyncThunk(
  'deleteContextualMessagesMerchantMapping',
  async (payload, { rejectWithValue }) => {
    try {
      const url = `${URL}/deletemerchantmapping`;
      const { data, status } = await apiConfig.post(url, payload);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createContextualMessagesMerchantMapping = createAsyncThunk(
  'createContextualMessagesMerchantMapping',
  async (payload, { rejectWithValue }) => {
    try {
      const url = `${URL}/merchantmapping`;
      const { data, status } = await apiConfig.put(url, payload);

      if (status !== 200) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const invalidateContextualMessagesMerchantMappingCache =
  createAsyncThunk(
    'invalidateContextualMessagesMerchantMappingCache',
    async (fetchFromDB = false, { rejectWithValue }) => {
      try {
        const url = `${URL}/merchantmapping/invalidatecache`;
        const { data, status } = await apiConfig.get(url);

        if (status !== 200) {
          return rejectWithValue(data);
        }

        return data;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
  );

const SLICE_NAME = 'paymentOptionsContextualMessages';

const initialState = {
  contextualMessagesMerchantMapping: [],
  contextualMessagesMerchantMappingStatus: STATUSES.IDLE, // 'idle' | 'loading' | 'succeeded' | 'failed',

  contextualMessagesMerchantMappingByChannelHash: [],
  contextualMessagesMerchantMappingByChannelHashStatus: STATUSES.IDLE, // 'idle' | 'loading' | 'succeeded' | 'failed',

  contextualMessageBannerList: [],
  contextualMessageBannerListStatus: STATUSES.IDLE, // 'idle' | 'loading' | 'succeeded' | 'failed',

  contextualMessageFormStatus: STATUSES.IDLE, // 'idle' | 'loading' | 'succeeded' | 'failed',
};

const paymentOptionsContextualMessageSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getContextualMessagesMerchantMapping.pending, (state) => {
        state.contextualMessagesMerchantMappingStatus = STATUSES.LOADING;
      })
      .addCase(getContextualMessagesMerchantMapping.rejected, (state) => {
        state.contextualMessagesMerchantMappingStatus = STATUSES.FAILED;
      })
      .addCase(
        getContextualMessagesMerchantMapping.fulfilled,
        (state, { payload }) => {
          state.contextualMessagesMerchantMapping = payload;
          state.contextualMessagesMerchantMappingStatus = STATUSES.SUCCEEDED;
        },
      );

    builder
      .addCase(
        getContextualMessagesMerchantMappingByChannelHash.pending,
        (state) => {
          state.contextualMessagesMerchantMappingByChannelHashStatus =
            STATUSES.LOADING;
        },
      )
      .addCase(
        getContextualMessagesMerchantMappingByChannelHash.rejected,
        (state) => {
          state.contextualMessagesMerchantMappingByChannelHashStatus =
            STATUSES.FAILED;
        },
      )
      .addCase(
        getContextualMessagesMerchantMappingByChannelHash.fulfilled,
        (state, { payload }) => {
          state.contextualMessagesMerchantMappingByChannelHash = payload;
          state.contextualMessagesMerchantMappingByChannelHashStatus =
            STATUSES.SUCCEEDED;
        },
      );

    builder
      .addCase(getContextualMessageBannerList.pending, (state) => {
        state.contextualMessageBannerListStatus = STATUSES.LOADING;
      })
      .addCase(getContextualMessageBannerList.rejected, (state) => {
        state.contextualMessageBannerListStatus = STATUSES.FAILED;
      })
      .addCase(
        getContextualMessageBannerList.fulfilled,
        (state, { payload }) => {
          state.contextualMessageBannerList = payload;
          state.contextualMessageBannerListStatus = STATUSES.SUCCEEDED;
        },
      );

    builder
      .addCase(createContextualMessagesMerchantMapping.pending, (state) => {
        state.contextualMessageFormStatus = STATUSES.LOADING;
      })
      .addCase(createContextualMessagesMerchantMapping.rejected, (state) => {
        state.contextualMessageFormStatus = STATUSES.FAILED;
      })
      .addCase(createContextualMessagesMerchantMapping.fulfilled, (state) => {
        state.contextualMessageFormStatus = STATUSES.SUCCEEDED;
      });
  },
});

export const paymentOptionsContextualMessageActions =
  paymentOptionsContextualMessageSlice.actions;

export const selectContextualMessageBannerList = (state) =>
  state.paymentOptions.contextualMessage.contextualMessageBannerList;

export const selectContextualMessagesMerchantMapping = (state) =>
  state.paymentOptions.contextualMessage.contextualMessagesMerchantMapping;

export const selectIsLoadingContextualMessagesMerchantMapping = (state) =>
  state.paymentOptions.contextualMessage
    .contextualMessagesMerchantMappingStatus === 'loading';

export const selectContextualMessagesMerchantMappingByChannelHash = (state) =>
  state.paymentOptions.contextualMessage
    .contextualMessagesMerchantMappingByChannelHash;

export const selectIsLoadingContextualMessagesMerchantMappingByChannelHash = (
  state,
) =>
  state.paymentOptions.contextualMessage
    .contextualMessagesMerchantMappingByChannelHashStatus === STATUSES.LOADING;

export const selectIsLoadingContextualMessageFormStatus = (state) =>
  state.paymentOptions.contextualMessage.contextualMessageFormStatus ===
  STATUSES.LOADING;

export default paymentOptionsContextualMessageSlice.reducer;
